import { DOCUMENT } from '@angular/common';
import { EventEmitter, Inject, Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { NavigationEnd, NavigationExtras, Router } from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  private notifications: Array<Notification>;
  private removedNotificationWithRoutingExtras = false;
  private renderer2: Renderer2;
  readonly onNotificationChange = new EventEmitter();

  /**
   * Emits true, after a notification with navigationExtras is removed
   * and the navigation is done.
   */
  readonly onNavigationEndAfterNotificationRemoval: EventEmitter<boolean> = new EventEmitter();

  constructor(
    protected router: Router,
    rendererFactory: RendererFactory2,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.renderer2 = rendererFactory.createRenderer(null, null);
    this.notifications = new Array<Notification>();
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd && this.removedNotificationWithRoutingExtras) {
        this.removedNotificationWithRoutingExtras = false;
        this.onNavigationEndAfterNotificationRemoval.emit(true);
      }
    });
  }

  /**
   * returns all undismissed notifications.
   */
  list(): Array<Notification> {
    return this.notifications;
  }

  getById(id: string) {
    return this.list().filter((notific: Notification) => notific.id === id);
  }

  /**
   * Allows to append a new notification
   * @param notification new notification to add. See Notification object for options
   */
  add(notification: Notification) {
    // only modify on the first notification, no need to do that over and over
    if (this.notifications.length < 1) {
      this.renderer2.setStyle(this.document.body, 'overflow', 'hidden');
    }
    this.notifications.push(notification);
    if (notification.displayTime > 1) {
      const index = this.notifications.length;
      setTimeout(() => {
        this.remove(index - 1);
      }, notification.displayTime);
    }
    this.onNotificationChange.emit();
  }

  /**
   * allows you to remove a notification at the given index.
   * @param index the index in the notification array you want to remove
   */
  remove(index: number) {
    if (this.notifications.length <= 1) {
      this.navigateOnNotification(0);
      this.notifications = [];
      this.renderer2.setStyle(this.document.body, 'overflow', 'initial');
    } else {
      this.navigateOnNotification(index);
      this.notifications.splice(index, 1);
    }
    this.onNotificationChange.emit();
  }

  /**
   * Navigates with the navigationExtras, if the notification-property navigationExtras exists.
   * @emits removedNotificationWithRoutingExtras
   * @param index
   */
  navigateOnNotification(index: number) {
    const notification = this.notifications[index];
    if (notification && Object.prototype.hasOwnProperty.call(notification, 'navigationExtras')) {
      this.removedNotificationWithRoutingExtras = true;
      this.router.navigate([], notification.navigationExtras);
    }
  }

  /**
   * removes all notification with a matching id
   * @param id the id of the notification(s)
   */
  removeById(id: string, immediately = false) {
    if (immediately) {
      this.notifications.forEach((notification, index) => {
        if (id.localeCompare(notification.id) === 0) {
          this.notifications.splice(index, 1);
          if (!this.notifications.length) {
            this.renderer2.setStyle(this.document.body, 'overflow', 'initial');
          }
          this.onNotificationChange.emit();
        }
      });
    } else {
      setTimeout(() => {
        this.notifications.forEach((notification, index) => {
          if (id.localeCompare(notification.id) === 0) {
            this.notifications.splice(index, 1);
            if (!this.notifications.length) {
              this.renderer2.setStyle(this.document.body, 'overflow', 'initial');
            }
            this.onNotificationChange.emit();
          }
        });
      }, 1000);
    }
  }
}

/**
 * translatable, auto-dismissable notification
 */
export class Notification {
  /**
   * if the notification should display an loading Indicator or not
   */
  public loadingIndicator?: boolean;

  /**
   * notification id to enable simple removal from list
   */
  public id?: string;

  /**
   * translated message you want to use. if transkey will be prefered if given.
   */
  public message?: string;

  /**
   * the translation key you want to use to get a translated message. you can add transvals if you want.
   */
  public transkey?: string;

  /**
   * translation values for the transkey if you need them.
   */
  public transvars?: { [key: string]: string | number };

  /**
   * NavigationExtras to navigate with. Will be called, when closing the notification
   */
  public navigationExtras?: NavigationExtras;

  /**
   * Time in milliseconds before automatic removal. Set to 0 to show 'close' button.
   */
  public displayTime = 1500;

  /**
   * if you want to display a confirmation of a given action, provide the translation for the confirm button and the eventEmitter that shall be called.
   */
  public confirmation?: Array<{
    type?: 'primary' | 'secondary';
    confirmationTrans: string;
    confirmationEmitter: EventEmitter<null>;
  }>;
}
